(ns app.front.view.group.admin.states
  (:require [app.domain.workspace.model :as workspace-model]
            [app.front.ui :as ui]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]
            [tilakone.core :as tk]))

(rf/reg-event-fx ::fetch-process-states
  (fn [_ [_ group-id permit-type process-flow]]
    {:comm/command {:command    :group/get-states
                    :method     :get
                    :data       (util/mmap group-id permit-type process-flow)
                    :on-success [::fetch-process-states.success group-id permit-type process-flow]}}))

(rf/reg-event-db ::fetch-process-states.success
  (fn [db [_ group-id permit-type process-flow _ {:keys [features state-machine subtypes]}]]
    (-> (assoc-in db [::states-data group-id permit-type process-flow :states] state-machine)
        (assoc-in [::states-data group-id permit-type process-flow :features] features)
        (assoc-in [::states-data group-id permit-type process-flow :subtypes] subtypes))))

(rf/reg-event-fx ::fetch-process-docs
  (fn [_ _]
    {:comm/command {:command    :admin/process-documentation
                    :method     :get
                    :on-success [::fetch-process-docs.success]}}))

(rf/reg-event-db ::fetch-process-docs.success
  (fn [db [_ status resp]]
    (cond-> db
      (= 200 status) (assoc ::docs resp))))

(rf/reg-sub ::all-states
  :-> ::states-data)
(rf/reg-sub ::docs
  :-> ::docs)

(rf/reg-sub ::states-of-group
  :<- [::all-states]
  :<- [::gas/group-id]
  (fn [[states group-id] _]
    (-> states
        (get group-id))))

(defn- target-keywords [group-configs _]
  (->> group-configs
       (keep :group.config/target)))

(rf/reg-sub ::permit-types
  :<- [::gas/root-group-configs]
  target-keywords)

(rf/reg-sub ::default-permit-type
  :<- [::permit-types]
  :-> first)

(rf/reg-sub ::states-for-permit-type
  :<- [::states-of-group]
  (fn [states [_ permit-type]]
    (-> states
        (get permit-type))))

(rf/reg-sub ::states
  (fn [[_ permit-type _ _]]
    (rf/subscribe [::states-for-permit-type permit-type]))
  (fn [states [_ _ process-flow entity-type]]
    (get-in states [process-flow :states entity-type])))

(rf/reg-sub ::features
  (fn [[_ permit-type _]]
    (rf/subscribe [::states-for-permit-type permit-type]))
  (fn [states [_ _ process-flow]]
    (get-in states [process-flow :features])))

(rf/reg-sub ::subtypes
  (fn [[_ permit-type _]]
    [(rf/subscribe [::states-for-permit-type permit-type])
     (rf/subscribe [:language])])
  (fn [[states lang] [_ _ process-flow]]
    (->> (get-in states [process-flow :subtypes])
         (map #(i18n-model/get-text-from-entities-or-map % lang))
         distinct
         (remove nil?)
         (remove empty?)
         sort)))

(rf/reg-sub ::documentation
  :<- [::docs]
  (fn [docs [_ guard-or-action]]
    (get docs guard-or-action)))

(defn get-actions-from-transitions [transitions]
  (->> transitions
       (map :tilakone.core/actions)
       (apply concat)))

(defn get-state-actions [state]
  (set (map first (concat
                    (-> state
                        :tilakone.core/enter
                        :tilakone.core/actions)
                    (-> state
                        :tilakone.core/leave
                        :tilakone.core/actions)
                    (get-actions-from-transitions
                      (get state :tilakone.core/transitions))))))

(rf/reg-sub ::actions
  (fn [[_ permit-type process-flow entity-type]]
    (rf/subscribe [::states permit-type process-flow entity-type]))
  (fn [states _]
    (-> (->> states
             (map get-state-actions)
             (apply concat))
        set
        sort)))

(rf/reg-event-db ::set-action-filter
  (fn [db [_ action-filter]]
    (if (nil? action-filter)
      (dissoc db ::action-filter)
      (assoc db ::action-filter action-filter))))

(rf/reg-sub ::selected-action-filter
  :-> ::action-filter)

(defn register-toggle [id default]
  (rf/reg-event-db (keyword (namespace id)
                            (->> id name (str "set-")))
    (fn [db [_ val]]
      (assoc db id val)))

  (rf/reg-sub id
    (fn [db _]
      (id db default))))

(register-toggle ::show-transitions true)
(register-toggle ::show-enter true)
(register-toggle ::only-state-change false)

(defn item-visualization [{:keys [class]} [key & attrs]]
  [:div.flex.space-between.mb-1.items-baseline
   [:h5.w-1-3 {:class class}
    (name key)
    (when-let [doc (<sub [::documentation key])]
      (cc/icon {:icon        i/circle-info
                :class-name  "ml-2"
                :description doc}))]
   (into [:div.flex.flex-col]
         (->> attrs
              (map str)
              (map (fn [s] [:span.font-mono s]))))])

(def guard-visualization (partial item-visualization {:class "text-ui-red-140"}))
(def action-visualization (partial item-visualization {:class "text-ui-blue-140"}))

(defn to-visualization [to]
  [:div.mb-2.text-ui-red-140.font-bold.flex.items-center
   (cc/icon {:icon        i/arrow-right
             :description "transition to"})
   [:span.ml-4 (name to)]])

(defn transition-visualization [{{::tk/keys [to actions guards] :as t} :transition
                                 :keys                                 [common-guards]}]
  (let [guards (seq (set/difference (set guards) common-guards))]
    (when (or to actions guards)
      [:li.mb-4.border-l-4.border-ui-violet.border-solid.p-2.mb-2.bg-ui-light-gray.mr-4.rounded-r-lg
       [:div.ml-2
        (let [other-props (dissoc t ::tk/on ::tk/guards ::tk/actions ::tk/to)]
          (when-not (empty? other-props)
            [:div.mb-2.font-mono.text-ui-blue (str other-props)]))
        (when to
          [to-visualization to])
        (when guards
          [:div
           [:h4 "Guards:"]
           [:ul
            (futil/for-frag [guard (sort-by (comp name first) guards)]
              [:li [guard-visualization guard]])]])
        (when (seq actions)
          [:div
           [:h4 "Actions:"]
           [:ul
            (futil/for-frag [action actions]
              [:li [action-visualization action]])]])]])))

(defn find-common-guards [transitions]
  (->> transitions
       (keep ::tk/guards)
       (map set)
       (reduce set/intersection)))

(defn action-list [{:keys [data title visible]}]
  (when (and (seq data) visible)
    [:div.mb-2
     [:h4 title]
     [:ul
      (futil/for-frag [action data]
        [:li [action-visualization action]])]]))

(defn state-visualization [{{:keys     [next-step-guards]
                             :as       s
                             ::tk/keys [transitions enter leave]} :state}]
  [:div
   (let [other-props (dissoc s ::tk/name ::tk/transitions :track :domain/state ::tk/enter ::tk/leave :next-step-guards)]
     (when-not (empty? other-props) (str other-props)))
   [action-list {:title   "Next step guards:"
                 :data    next-step-guards
                 :visible (<sub [::show-enter])}]
   [action-list {:title   "On enter:"
                 :data    (::tk/actions enter)
                 :visible (<sub [::show-enter])}]
   [action-list {:title   "On leave:"
                 :data    (::tk/actions leave)
                 :visible (<sub [::show-enter])}]
   (if (<sub [::show-transitions])
     [:ul.ml-4
      (futil/for-frag [[signal transitions] (group-by ::tk/on transitions)]
        (when (or (not (<sub [::only-state-change]))
                  (->> transitions
                       (some ::tk/to)))
          (let [common-guards (find-common-guards transitions)
                actions       (->> transitions
                                   (map ::tk/actions)
                                   (apply concat)
                                   (map first)
                                   set)
                action-filter (<sub [::selected-action-filter])]
            (when (or (not action-filter) (contains? actions action-filter))
              [:li.border-2.border-ui-violet.border-solid.mb-4.rounded-lg.bg-ui-violet-20
               [:h3.pl-2 signal]
               [:div.mb-2.pl-4
                [:h4 "Common guards"]
                [:ul
                 (futil/for-frag [guard (sort-by (comp name first) common-guards)]
                   [:li [guard-visualization guard]])]]
               [:ul
                (futil/for-frag [transition transitions]
                  [transition-visualization (util/mmap transition common-guards)])]]))))]
     [:ul
      (futil/for-frag [destination (->> transitions
                                        (keep ::tk/to)
                                        distinct)]
        [:li [to-visualization destination]])])])

(defn toggle [{:keys [check-event value-sub] :as props}]
  (cc/checkbox (-> props
                   (select-keys [:id :label-text])
                   (merge {:wrapper-class-name "flex-none"
                           :checked            (<sub [value-sub])
                           :on-change          #(>evt [check-event %])}))))

(def aspect 3.0)

(defn- get-state [states k]
  (-> states
      (p/find-first #(-> % :tilakone.core/name (= k)))))

(defn- get-outgoing-states [hovered-state]
  (->> hovered-state
       :tilakone.core/transitions
       (map :tilakone.core/to)
       set))

(def gap
  0.1)

(def width
  (- 1.0 (* 2 gap)))

(defn visualizer [{:keys [states selected-state on-select-state]}]
  (r/with-let [*hover (r/atom nil)]
    (let [tr                  (<sub [:tr])
          tracks              (->> states (group-by :track))
          n-tracks            (count tracks)
          n-states            (some->> tracks vals seq (map count) (apply max))
          k                   @*hover
          hovered-state       (get-state states k)
          selected-state-def  (get-state states selected-state)
          to-states           (get-outgoing-states hovered-state)
          to-states-selected  (get-outgoing-states selected-state-def)
          action-filter       (<sub [::selected-action-filter])
          hovered-or-selected (or hovered-state selected-state-def)]
      (when (seq states)
        [:div
         [:div.mb-2
          [:h4
           (if-let [s (:domain/state hovered-or-selected)]
             (str (tr [s]) " (" s ")")
             (or (some-> hovered-or-selected :tilakone.core/name name) "-"))]
          (if-let [track (:track hovered-or-selected)]
            [:div [:span.bold.mr-1 "Track:"] (name track)]
            [:div (if hovered-or-selected "Default track" "-")])]
         [:svg {:viewBox (str/join " " ["0 0" n-states (/ n-tracks aspect)])
                :style   {:width "100%"}}
          (->> tracks
               (map-indexed (fn [ix-track [track states]]
                              (->> states
                                   (map-indexed (fn [ix-state state]
                                                  (let [my-key           (:tilakone.core/name state)
                                                        state-name       (name my-key)
                                                        can-proceed?     (contains? to-states my-key)
                                                        can-proceed-sel? (contains? to-states-selected my-key)
                                                        selected?        (= my-key selected-state)
                                                        default-track?   (-> state :track nil?)
                                                        stroke           (cond
                                                                           (= k my-key) "blue"
                                                                           can-proceed? "green")
                                                        filter?          (contains? (get-state-actions state) action-filter)]
                                                    [:rect {:id            state-name
                                                            :alt           state-name
                                                            :style         {:stroke       stroke
                                                                            :fill         (cond
                                                                                            selected?        "blue"
                                                                                            can-proceed-sel? "green"
                                                                                            default-track?   "#909090"
                                                                                            :else            "#bababa")
                                                                            :stroke-width 0.05
                                                                            :opacity      (if (or (nil? action-filter)
                                                                                                  filter?)
                                                                                            1.0
                                                                                            0.3)
                                                                            :rx           (when stroke gap)
                                                                            :ry           (when stroke gap)}
                                                            :x             (+ ix-state gap)
                                                            :y             (+ (/ ix-track aspect) (/ gap aspect))
                                                            :width         width
                                                            :height        (/ width aspect)
                                                            :on-click      #(on-select-state my-key)
                                                            :on-mouse-out  #(reset! *hover nil)
                                                            :on-mouse-over #(reset! *hover my-key)}])))
                                   (into (let [id (or (some-> track name) "default")]
                                           [:g {:id  id
                                                :key id}]))))))]]))))

(defn additional-info-box [{:keys [header info rows]}]
  [:div.flex-1.p-2.rounded-md.mt-2.mr-4.border.border-gray-500.border-solid
   [:h4.m-2 header]
   [ui/help info]
   [:div.grid.grid-cols-2.gap-2
    (futil/for-frag [row rows]
      [:div.m-2 (name row)])]])

(defn view [{{{:keys [group-id]} :path} :parameters
             :as                        match}]
  (r/with-let [*type           (r/atom nil)
               *process-flow   (r/atom nil)
               *entity         (r/atom :entity.type/application)
               *selected-state (r/atom nil)]
    (let [permit-types   (<sub [::permit-types])
          permit-type    (or ((set permit-types) @*type)
                             (<sub [::default-permit-type]))
          process-flow   @*process-flow
          action-filter  (<sub [::selected-action-filter])
          entity-type    @*entity
          states         (<sub [::states permit-type process-flow entity-type])
          selected-state @*selected-state
          tr             (<sub [:tr])
          process-flows  (when permit-type
                           (<sub [::gas/process-flows permit-type]))
          features       (<sub [::features permit-type process-flow])
          subtypes       (<sub [::subtypes permit-type process-flow])]
      [acommon/admin-page
       (util/mmap {:title "State machines"} match)
       [acommon/section
        {:title "View process"}
        [ui/help [:div
                  "Instructions on how to use this view can be found "
                  [:a {:href       "https://cloudpermit.atlassian.net/wiki/x/AQDD1g"
                       :target     "_blank"
                       :aria-label (tr [:process/view-process])}
                   "here"]]]
        [:div.flex.flex-wrap.mb-2
         [ui-cs/content-switcher {:items     permit-types
                                  :val->str  #(tr [%])
                                  :class     "flex-1 mt-6 mb-4"
                                  :current   permit-type
                                  :on-change (partial reset! *type)}]
         [:div.flex-0 {:class (when (-> permit-types count (> 1)) "ml-4")}
          [ui-select/select {:values       process-flows
                             :allow-empty? false
                             :required?    true
                             :id           "process-flow"
                             :value        process-flow
                             :val->str     #(tr [%])
                             :label-text   "Process flow"
                             :on-change    (fn [new-process-flow]
                                             (reset! *process-flow new-process-flow)
                                             (>evt [::fetch-process-states group-id permit-type new-process-flow]))}]]
         [:div.flex
          (when (seq features)
            [additional-info-box (util/mmap {:header "Features/components"
                                             :info   "Group features that are explicitly enabled for the selected process flow. However,
                                                     this is not a watertight list since the actions listed in the dropdown
                                                     below ultimately define the features in use."
                                             :rows   features}
                                            tr)])
          (when (seq subtypes)
            [additional-info-box (util/mmap {:header "Subtypes using this process flow"
                                             :info   "Permit subtypes that use the selected process flow."
                                             :rows   subtypes}
                                            tr)])]]
        [ui-cs/content-switcher {:items     workspace-model/phase-entity-types
                                 :val->str  #(tr [%])
                                 :current   @*entity
                                 :on-change (partial reset! *entity)
                                 :class     "mb-4"}]
        [:div.flex
         [:div.flex.flex-col.w-80
          [toggle {:id          "show-enter-actions"
                   :label-text  "Show 'On enter' and 'On leave' actions"
                   :value-sub   ::show-enter
                   :check-event ::set-show-enter}]
          [toggle {:id          "show-transitions"
                   :label-text  "Show transitions"
                   :value-sub   ::show-transitions
                   :check-event ::set-show-transitions}]
          (when (<sub [::show-transitions])
            [toggle {:id          "only-state-changes"
                     :label-text  "Show only transitions that change state"
                     :value-sub   ::only-state-change
                     :check-event ::set-only-state-change}])

          [:div.flex
           [:div.flex-1
            [ui-select/select {:id           "action-filter"
                               :values       (<sub [::actions permit-type process-flow @*entity])
                               :class        "mt-3"
                               :label-text   "Filter states by actions"
                               :val->str     name
                               :light        true
                               :allow-empty? true
                               :value        action-filter
                               :on-change    #(>evt [::set-action-filter %])}]]
           [:div.flex-1.pt-10.ml-2
            (cc/button {:size     :small
                        :kind     :tertiary
                        :on-click #(>evt [::set-action-filter nil])}
                       "Clear")]]
          (when action-filter
            [:div.mt-4
             [:h6.mb-2.font-mono (str action-filter)]
             [:div.font-mono (<sub [::documentation action-filter])]])]
         [:div.flex-1
          (when (seq states)
            [:<>
             [visualizer (util/mmap {:on-select-state #(reset! *selected-state %)}
                                    states
                                    selected-state)]
             (if selected-state
               (let [state (get-state states selected-state)]
                 [:div
                  [:h2 (if-let [domain-state (:domain/state state)]
                         (tr [domain-state]) (some-> state :tilakone.core/name name))]
                  [state-visualization (util/mmap state)]])
               [:div "Select a state above"])])]]]])))

(def route
  {:name        :view/group.admin.states
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [_]
                                (>evt [::fetch-process-docs]))}]})
